// PKS config fetcher. At boot, the host tries to fetch product-line declarations
// from gaxl-platform-knowledge-service (PKS). On failure, falls back to
// window.RUNTIME_CONFIG.UI_PRODUCT_LINES from public/envConfig.js.

import type { AdapterInitConfig, ProductLineDeclaration } from './AdapterRegistry';
import { PKS_API_PATHS } from '../constants/apiPaths';
import { PKS_FETCH_TIMEOUT_MS } from '../constants/timing';

interface PksProductLineResponse {
  productLines: ProductLineDeclaration[];
  defaultProductLineId?: string;
}

/**
 * Resolve the AdapterInitConfig: PKS first, envConfig.js fallback.
 *
 * When UI_PKS_URL is set:
 *   GET {UI_PKS_URL}/api/pks/v1/product-lines
 */
export async function fetchAdapterConfig(): Promise<AdapterInitConfig> {
  const runtime = window.RUNTIME_CONFIG ?? {};
  const pksBaseUrl = runtime.UI_PKS_URL;

  if (pksBaseUrl) {
    try {
      const url = `${pksBaseUrl.replace(/\/$/, '')}${PKS_API_PATHS.PRODUCT_LINES}`;
      const res = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(PKS_FETCH_TIMEOUT_MS),
      });

      if (res.ok) {
        const data = (await res.json()) as PksProductLineResponse;
        if (data.productLines?.length) {
          return {
            productLines: data.productLines,
            defaultProductLineId: data.defaultProductLineId ?? runtime.UI_DEFAULT_PRODUCT_LINE,
          };
        }
      }

      console.warn('[fetchConfig] PKS responded %d — falling back to envConfig.js', res.status);
    } catch (err) {
      console.warn('[fetchConfig] PKS unreachable — falling back to envConfig.js', err);
    }
  }

  return {
    productLines: runtime.UI_PRODUCT_LINES ?? [],
    defaultProductLineId: runtime.UI_DEFAULT_PRODUCT_LINE,
  };
}
